import platform from '@play-co/gcinstant';
import statePromise from 'src/lib/statePromise';
import { isSceneEntered } from 'src/lib/stateUtils';
import StateObserver from 'src/StateObserver';
import { ActionSequence } from '../../lib/ActionSequence';
import {
  getSceneManager,
  waitForAssets,
  waitForItPromise,
  withLoading,
} from '../../lib/utils';
import { EntityManager } from '../logic/EntityManager';
import { petSpeciesAssets } from 'src/loadingGroups';
import {
  isPetSpeaking,
  startConversation,
} from '../conversation/conversation.redux';
import { Pet, PetEvolutionStage, PetSpecies } from 'src/replicant/buruburu/pet';
import { Visit } from 'src/replicant/visit';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { getFriendsPet, getPet, setFriendsPet } from '../pet';
import { PetController } from '../pet/PetController';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import { getTutorialController } from '../tutorial/TutorialController';
import { duration } from 'src/replicant/utils/duration';
import { Position } from '../position/PositionModel';
import { StripPromise } from '@play-co/replicant/lib/core/utils/TypeUtils';

export async function startVisitSequence(selectedTab?: 'friends' | 'visitors') {
  let friendId: string;
  let attributesUpdater: any;

  await ActionSequence.start(
    [
      async function showTutorial() {
        // Show tutorial.
        await getTutorialController().handleEvent('visitPopupOpened');
        return false;
      },

      async function selectFriendToVisit() {
        // Fetch friend list data
        const { visitListLimit } = Visit.ruleset;
        const { asyncGetters } = StateObserver.replicant;
        const [friends, visitors] = await withLoading(() =>
          Promise.all([
            asyncGetters.getVisitFriendsList(visitListLimit),
            asyncGetters.getRecentVisitorList(visitListLimit),
          ]),
        );

        // Continually prompt user to select friend to visit
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // Show friend selection popup
          const selectedFriend = await openPopupPromise('popupVisitFriend', {
            friends,
            visitors,
            selectedTab,
          });
          if (!selectedFriend) {
            // User pressed cancel, so break out of this sequence
            return true;
          }

          // Get friend details.
          const details = await withLoading(() =>
            asyncGetters.getPlayerVisitDetails(selectedFriend),
          );

          // Target pet for visit is dead, show busy dialog
          if (details.state === 'dead') {
            await openPopupPromise('popupModalWhite', {
              title: i18n('friendsVisit.busy.title'),
              message: i18n('friendsVisit.busy.message'),
              tracking: {
                feature: Feature.VISIT._,
                $subFeature: Feature.VISIT.POPUP_FRIEND_DEAD,
              },
            });
            return true;
          }

          // Bail if friend is online.
          if (details.isOnline) {
            await openPopupPromise('popupModalWhite', {
              title: i18n('friendsVisit.busy.title'),
              message: i18n('friendsVisit.busy.message'),
              tracking: {
                feature: Feature.VISIT._,
                $subFeature: Feature.VISIT.POPUP_FRIEND_BUSY,
              },
            });
            return true;
          }

          // Show visit confirmation popup
          const confirmFriend = await openPopupPromise(
            'popupConfirmVisitFriend',
            details,
          );
          if (confirmFriend) {
            // User confirmed friend, so hand over to next sequence step
            friendId = selectedFriend;
            return false;
          }
        }
      },

      async function beginVisitSession() {
        if (await startVisitSession({ friendId })) {
          return true;
        }

        const state = StateObserver.getState();
        const { user } = state;
        const { getCurrentEvolution } = Pet.getters(user);

        await startConversation(
          platform.playerID,
          'friend_greetings',
          getCurrentEvolution().species,
          'normal', // The pet state is not relevant for this dialog.
        );

        await startConversation(
          friendId,
          'friend_welcome',
          state.pet[friendId].species,
          'normal', // The pet state is not relevant for this dialog.
        );

        // Update friend pet attributes in realtime
        attributesUpdater = setInterval(async () => {
          const attributes = await StateObserver.replicant.asyncGetters.getFriendAttributeValues(
            friendId,
          );

          getFriendsPet().appearance.setAttributes(attributes);
        }, duration({ seconds: 10 }));

        return false;
      },

      async function endVisitIfKickedOut() {
        // Wait until player ends the visit session or is kicked out by the friend
        await statePromise(
          ({ user }) =>
            !user.visit.visitSession || user.visit.visitSession.isKickedOut,
        );

        // Stop updating friends attributes
        clearInterval(attributesUpdater);

        const { user } = StateObserver.getState();
        const { getCurrentEvolution } = Pet.getters(user);

        if (!Visit.getters(user).isVisiting()) {
          // Player ended the visit session so return normally
          return false;
        }

        // Friend kicked us out
        // Wait until we're idling in the living room
        await statePromise(
          ({ care }) =>
            isSceneEntered('friendsLivingRoom') &&
            !care.currentCareAction &&
            !isPetSpeaking(),
        );

        // Show kicked out dialog
        await startConversation(
          platform.playerID,
          'friend_kickedout',
          getCurrentEvolution().species,
          'normal', // The pet state is not relevant for this dialog.
        );

        // End visit
        const { visitSession } = user.visit;
        await endVisitSession(visitSession?.friendId);

        return true;
      },
    ],
    'VisitFriend',
  );
}

export async function startVisitSession({
  friendId,
  playerTargetPosition,
  friendTargetPosition,
  overrides,
}: {
  friendId: string;
  playerTargetPosition?: Position;
  friendTargetPosition?: Position;
  overrides?: Partial<{
    evolution: { species: PetSpecies; stage: PetEvolutionStage };
    age: number;
    petName: string;
  }>;
}) {
  // If there's an active session try to end it.
  if (StateObserver.getState().user.visit.visitSession) {
    await withLoading(() => StateObserver.invoke.asyncEndVisit(true));
  }

  // Ensure we've selected a friend before coming here
  if (!friendId) {
    return true;
  }

  const data = await withLoading(() =>
    StateObserver.invoke.asyncBeginVisit(friendId),
  );

  // Apply overrides.
  if (overrides) {
    data.age = overrides.age ?? data.age;
    data.attributes.name = overrides.petName ?? data.attributes.name;
    Object.assign(data.currentEvolution, overrides.evolution);
  }

  // Load friend's species assets.
  await waitForAssets(petSpeciesAssets[data.currentEvolution?.species]);

  const friendPet = EntityManager.createOrGetFriendsPet(friendId);
  const friendPetController = new PetController(getSceneManager(), friendPet);
  setFriendsPet(friendPetController);

  friendPetController.init({
    pet: {
      species: data.currentEvolution?.species,
      statusEffects: data.statusEffects,
      attributes: {
        ...data.attributes,
        age: data.age,
        isUpdating: false,
      },
    },
    level: { current: data.level, xp: data.xp },
  });

  const petController = getPet();

  await Promise.all([
    getPet().moveTo(
      playerTargetPosition ?? {
        scene: 'friendsLivingRoom',
        location: 'lm',
      },
    ),
    friendPetController.moveTo(
      friendTargetPosition ?? {
        scene: 'friendsLivingRoom',
        location: 'rm',
      },
      0,
    ),
  ]);
  await waitForItPromise();

  petController.appearance.setAction(null);

  return false;
}

export async function endVisitSession(
  friendId: string,
  transitionToLivingRoom: boolean = true,
  sendMessage: boolean = true,
) {
  if (!friendId) {
    return;
  }

  // End visit session.
  await withLoading(() => StateObserver.invoke.asyncEndVisit(sendMessage));

  if (transitionToLivingRoom) {
    const pet = getPet();
    const { appearance } = pet;

    // Transition back to living room
    await pet.moveTo({
      scene: 'livingRoom',
      location: 'c',
    });

    appearance.setAction(null);
  }

  // Dispose of friend's pet.

  getFriendsPet().destroy();
  setFriendsPet(null);
}
